import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faGlobe, 
  faBell,
  faImage,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import adminService from '../../services/adminService';
import axios from 'axios';

function AdminSettings() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [authError, setAuthError] = useState(false);
  
  const [generalSettings, setGeneralSettings] = useState({
    contactEmail: 'info@bangalorevaluers.org',
    contactPhone: '+91 80 2345 6789',
    address: '522/C, 1st D Cross, 16th Main, 3rd Stage, 4th Block, Basaveshwaranagar, Bangalore - 560 079'
  });
  
  const [notificationSettings, setNotificationSettings] = useState({
    newMemberNotification: true,
    newPaymentNotification: true,
    programReminderNotification: true
  });
  
  const [socialMediaSettings, setSocialMediaSettings] = useState({
    facebook: 'https://facebook.com/bangalorevaluers',
    twitter: 'https://twitter.com/bvaluers',
    linkedin: 'https://linkedin.com/company/bangalore-valuers-association',
    youtube: ''
  });
  
  useEffect(() => {
    fetchSettings();
  }, []);
  
  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        setAuthError(true);
        setError('Authentication token not found. Please login again.');
        setTimeout(() => {
          navigate('/admin/login');
        }, 3000);
        return;
      }
      
      // Check if token is expired
      const tokenExpires = localStorage.getItem('adminTokenExpires');
      if (tokenExpires && new Date(tokenExpires) <= new Date()) {
        setAuthError(true);
        setError('Your session has expired. Please login again.');
        setTimeout(() => {
          navigate('/admin/login');
        }, 3000);
        return;
      }
      
      try {
        // Use adminService directly without checking for auth token again
        const settings = await adminService.getSettings();
        
        setGeneralSettings(settings.general || generalSettings);
        setNotificationSettings(settings.notifications || notificationSettings);
        setSocialMediaSettings(settings.socialMedia || socialMediaSettings);
      } catch (apiError) {
        console.error('Error fetching settings:', apiError);
        
        if (axios.isAxiosError(apiError) && apiError.response?.status === 401) {
          setAuthError(true);
          setError('Your session has expired. Please login again.');
          setTimeout(() => {
            navigate('/admin/login');
          }, 3000);
        } else {
          setError('Failed to load settings. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error in fetch settings:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleGeneralChange = (e) => {
    const { name, value } = e.target;
    setGeneralSettings(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  const handleNotificationChange = (e) => {
    const { name, checked } = e.target;
    setNotificationSettings(prevState => ({
      ...prevState,
      [name]: checked
    }));
  };
  
  const handleSocialMediaChange = (e) => {
    const { name, value } = e.target;
    setSocialMediaSettings(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccessMessage('');
    
    try {
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        setAuthError(true);
        setError('Authentication token not found. Please login again.');
        setTimeout(() => {
          navigate('/admin/login');
        }, 3000);
        return;
      }

      // Check if token is expired
      const tokenExpires = localStorage.getItem('adminTokenExpires');
      if (tokenExpires && new Date(tokenExpires) <= new Date()) {
        setAuthError(true);
        setError('Your session has expired. Please login again.');
        setTimeout(() => {
          navigate('/admin/login');
        }, 3000);
        return;
      }
      
      await adminService.updateSettings({
        general: generalSettings,
        notifications: notificationSettings,
        socialMedia: socialMediaSettings
      });
      
      setSuccessMessage('Settings saved successfully!');
      
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (error) {
      console.error('Error saving settings:', error);
      
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        setAuthError(true);
        setError('Your session has expired. Please login again.');
        setTimeout(() => {
          navigate('/admin/login');
        }, 3000);
      } else {
        setError('Failed to save settings. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };
  
  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="admin-form">
            <div className="admin-form-row">
              <div className="admin-form-group">
                <label htmlFor="contactEmail">Contact Email</label>
                <input
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  value={generalSettings.contactEmail}
                  onChange={handleGeneralChange}
                  className="admin-form-control"
                />
              </div>
              
              <div className="admin-form-group">
                <label htmlFor="contactPhone">Contact Phone</label>
                <input
                  type="text"
                  id="contactPhone"
                  name="contactPhone"
                  value={generalSettings.contactPhone}
                  onChange={handleGeneralChange}
                  className="admin-form-control"
                />
              </div>
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="address">Address</label>
              <textarea
                id="address"
                name="address"
                value={generalSettings.address}
                onChange={handleGeneralChange}
                className="admin-form-control"
                rows={3}
              />
            </div>
          </div>
        );
      
      case 'notifications':
        return (
          <div className="admin-form">
            <div className="admin-form-group">
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <input
                  type="checkbox"
                  id="newMemberNotification"
                  name="newMemberNotification"
                  checked={notificationSettings.newMemberNotification}
                  onChange={handleNotificationChange}
                  style={{ marginRight: '0.5rem' }}
                />
                <label htmlFor="newMemberNotification">
                  <strong>New Member Notifications</strong><br />
                  <span style={{ color: '#6c757d', fontSize: '0.875rem' }}>Notify administrators when a new member registers</span>
                </label>
              </div>
            </div>
            
            <div className="admin-form-group">
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <input
                  type="checkbox"
                  id="newPaymentNotification"
                  name="newPaymentNotification"
                  checked={notificationSettings.newPaymentNotification}
                  onChange={handleNotificationChange}
                  style={{ marginRight: '0.5rem' }}
                />
                <label htmlFor="newPaymentNotification">
                  <strong>Payment Notifications</strong><br />
                  <span style={{ color: '#6c757d', fontSize: '0.875rem' }}>Notify administrators about new payments</span>
                </label>
              </div>
            </div>
            
            <div className="admin-form-group">
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <input
                  type="checkbox"
                  id="programReminderNotification"
                  name="programReminderNotification"
                  checked={notificationSettings.programReminderNotification}
                  onChange={handleNotificationChange}
                  style={{ marginRight: '0.5rem' }}
                />
                <label htmlFor="programReminderNotification">
                  <strong>Program Reminders</strong><br />
                  <span style={{ color: '#6c757d', fontSize: '0.875rem' }}>Send reminder emails to members about upcoming programs</span>
                </label>
              </div>
            </div>
          </div>
        );
      
      case 'social':
        return (
          <div className="admin-form">
            <div className="admin-form-group">
              <label htmlFor="facebook">Facebook</label>
              <input
                type="url"
                id="facebook"
                name="facebook"
                value={socialMediaSettings.facebook}
                onChange={handleSocialMediaChange}
                className="admin-form-control"
                placeholder="https://facebook.com/..."
              />
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="twitter">Twitter</label>
              <input
                type="url"
                id="twitter"
                name="twitter"
                value={socialMediaSettings.twitter}
                onChange={handleSocialMediaChange}
                className="admin-form-control"
                placeholder="https://twitter.com/..."
              />
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="linkedin">LinkedIn</label>
              <input
                type="url"
                id="linkedin"
                name="linkedin"
                value={socialMediaSettings.linkedin}
                onChange={handleSocialMediaChange}
                className="admin-form-control"
                placeholder="https://linkedin.com/company/..."
              />
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="youtube">YouTube</label>
              <input
                type="url"
                id="youtube"
                name="youtube"
                value={socialMediaSettings.youtube}
                onChange={handleSocialMediaChange}
                className="admin-form-control"
                placeholder="https://youtube.com/..."
              />
            </div>
          </div>
        );
      
      default:
        return <div>Unknown tab</div>;
    }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Settings</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard" className="admin-breadcrumb-link">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item active">
            <span className="admin-breadcrumb-link">Settings</span>
          </li>
        </ul>
      </div>
      
      {authError && (
        <div style={{ 
          position: 'fixed', 
          top: '1rem', 
          right: '1rem',
          padding: '0.75rem 1.25rem',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          maxWidth: '90%',
          width: '400px'
        }}>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          Your session has expired. Redirecting to login...
        </div>
      )}
      
      {error && !authError && (
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '0.75rem 1.25rem', 
          marginBottom: '1rem', 
          borderRadius: '0.25rem' 
        }}>
          {error}
        </div>
      )}
      
      {successMessage && (
        <div style={{ 
          backgroundColor: '#d4edda', 
          color: '#155724', 
          padding: '0.75rem 1.25rem', 
          marginBottom: '1rem', 
          borderRadius: '0.25rem' 
        }}>
          {successMessage}
        </div>
      )}
      
      <div className="admin-card">
        <div className="admin-card-body" style={{ padding: '0' }}>
          <div className="admin-settings-tabs" style={{ display: 'flex' }}>
            <div className="admin-settings-tab-sidebar" style={{ 
              width: '250px', 
              borderRight: '1px solid #dee2e6',
              padding: '1.5rem 0'
            }}>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <a 
                    href="#general" 
                    onClick={(e) => { e.preventDefault(); setActiveTab('general'); }}
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.75rem 1.5rem',
                      textDecoration: 'none',
                      color: activeTab === 'general' ? 'var(--admin-primary)' : '#6c757d',
                      backgroundColor: activeTab === 'general' ? '#f8f9fa' : 'transparent',
                      fontWeight: activeTab === 'general' ? '600' : '400'
                    }}
                  >
                    <FontAwesomeIcon icon={faGlobe} style={{ marginRight: '0.75rem', width: '20px' }} />
                    General Settings
                  </a>
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <a 
                    href="#notifications" 
                    onClick={(e) => { e.preventDefault(); setActiveTab('notifications'); }}
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.75rem 1.5rem',
                      textDecoration: 'none',
                      color: activeTab === 'notifications' ? 'var(--admin-primary)' : '#6c757d',
                      backgroundColor: activeTab === 'notifications' ? '#f8f9fa' : 'transparent',
                      fontWeight: activeTab === 'notifications' ? '600' : '400'
                    }}
                  >
                    <FontAwesomeIcon icon={faBell} style={{ marginRight: '0.75rem', width: '20px' }} />
                    Notifications
                  </a>
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <a 
                    href="#social" 
                    onClick={(e) => { e.preventDefault(); setActiveTab('social'); }}
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.75rem 1.5rem',
                      textDecoration: 'none',
                      color: activeTab === 'social' ? 'var(--admin-primary)' : '#6c757d',
                      backgroundColor: activeTab === 'social' ? '#f8f9fa' : 'transparent',
                      fontWeight: activeTab === 'social' ? '600' : '400'
                    }}
                  >
                    <FontAwesomeIcon icon={faImage} style={{ marginRight: '0.75rem', width: '20px' }} />
                    Social Media
                  </a>
                </li>
              </ul>
            </div>
            
            <div className="admin-settings-tab-content" style={{ flex: '1', padding: '1.5rem' }}>
              {isLoading ? (
                <div style={{ padding: '2rem', textAlign: 'center' }}>Loading settings...</div>
              ) : (
                <form onSubmit={handleSaveSettings}>
                  <h2 style={{ marginBottom: '1.5rem', color: 'var(--admin-primary)' }}>
                    {activeTab === 'general' && 'General Settings'}
                    {activeTab === 'notifications' && 'Notification Settings'}
                    {activeTab === 'social' && 'Social Media Settings'}
                  </h2>
                  
                  {renderTabContent()}
                  
                  <div className="admin-form-actions" style={{ marginTop: '2rem' }}>
                    <button
                      type="submit"
                      className="admin-btn admin-btn-primary"
                      style={{ width: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                      disabled={isSaving}
                    >
                      <FontAwesomeIcon icon={faSave} />
                      {isSaving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminSettings;
